import { Collection, CollectionTarget } from "@ciaopeople/cp-shatsu/DomHelper";
import { SdkHandledHTMLElement } from "./SdkHandledHTMLElement.js";
import { DomHelper as D } from "@ciaopeople/cp-shatsu/DomHelper";
import { inEnum, isArray, isObject } from "@ciaopeople/cp-shatsu/TypeChecking";
import { flatten, hasOwnProperty } from "@ciaopeople/cp-shatsu/Utils";
import { GroupableData, isGroupableData } from "../../api/commondata/GroupableData.js";
import { TypeGuard } from '@ciaopeople/cp-shatsu/CustomTypes';
import { Subject } from "@ciaopeople/cp-shatsu/Observable";
import { UserActionError, UserActionIdentifier } from "../../api/commondata/UserActionError.js";
import { ContentRestrictionCriteria } from "../../api/types/NotificationDataTypes.js";


export enum ActionableEvents {
    click = "click"
}


export interface ElementWithAttachData<StatusData, T extends GroupableData> {
    element: SdkHandledHTMLElement<StatusData>,
    attachData: T
}

export interface TaintedElementWithAttachData<StatusData, T extends GroupableData> extends ElementWithAttachData<StatusData, T> {
    action: UserActionIdentifier
}

export function isElementWithAttachData<StatusData, T extends GroupableData>(obj: any, isStatusData: TypeGuard<StatusData>): obj is ElementWithAttachData<StatusData, T> {
    return isObject(obj) && isStatusData(obj.attachData) && hasOwnProperty(obj, 'element') && obj.element instanceof SdkHandledHTMLElement;
}

export interface AttachAndStatusData <S, T extends GroupableData> {
    attachData: T,
    status: S,
    restriction?: ContentRestrictionCriteria
}

export function isAttachAndStatusData<S, T extends GroupableData>(obj: any, isStatus: TypeGuard<S>): obj is AttachAndStatusData<S, T> {
    return isObject(obj) && isStatus(obj.status) && isGroupableData(obj.attachData) && 
        (!("restriction" in obj) || inEnum(obj.restriction,ContentRestrictionCriteria));
}


export abstract class ParseAndAttachModule <StatusData, T extends GroupableData> {

 
    protected groupedElements: { [k: string]: ElementWithAttachData<StatusData, T>[] } =  {};


    public readonly success: Subject<TaintedElementWithAttachData<StatusData, T>> = new Subject();
    public readonly errors:  Subject<UserActionError> = new Subject();
    
    
    

    /**
     * estrae i dati e crea l'elemento con i dati parsati
     * torna null se non è un elemento formalmente valido
     * @param el 
     */
    protected abstract extractAndCreate(el: HTMLElement): ElementWithAttachData<StatusData, T>|null;
    
    /**
     * azione da compiere sul "click" o altro evento di interazione dell'utente
     * con l'elemento attaccato
     * @param el 
     */
    public abstract action(el: ElementWithAttachData<StatusData, T>): void;
    //public abstract action(el: ElementWithAttachData<StatusData, T>): void;
        
    /**
     * azione da compiere dopo aver fatto il parse e l'attach
     * dei componenti
     * @param newlyAttached i nuovi elementi aggiunti dopo l'invocazione del parse
     */
    protected abstract afterAttach(newlyAttached: ElementWithAttachData<StatusData, T>[]): Promise<void>;


    
    public refreshElementsStatus(): Promise<void> {       
        return this.afterAttach(Object.values(this.groupedElements).map(e => e[0]));
    }

    /**
     * viene invocato dopo l'aggiornamento di stato riportando gli elementi del dom coinvolti 
     * nell'aggiornamento. utile per rilanciare ad esempio gli eventi `topicFollowStatusChange` arricchiti
     * con gli elementi del dom coinvolti (così come utilizzato oggi dall'integrazione lato wp)
     * 
     * @param s dati di aggiornamento di stato originari (utilizzati per aggiornare lo stato degli elementi)
     * @param involvedElements gli elementi del dom coinvolti nell'aggiornamento di stato
     */
    protected abstract afterUpdate(s: AttachAndStatusData<StatusData, T>, involvedElements: SdkHandledHTMLElement<StatusData>[]): void;


    /**
     * effettui il parse e l'attach della action sugli elementi
     * designati
     * @param el elementi da parsare / fare attach 
     * @param trigger evento su cui innescare la action, se è null non viene effettuato attack
     */
    public parseAndAttach(el: CollectionTarget | CollectionTarget[], trigger:  ActionableEvents | null): Promise<void> {
        type T = Collection | Element | string;
        const useEl: T|T[] = [];
        if (isArray(el)) {
            el.forEach(
                el => {
                    if (isArray(el)) {
                        useEl.push(...el);
                    }
                    else {
                        useEl.push(el);
                    }
                }
            );
        }
        else {
            useEl.push(el);
        }
        const collection: HTMLElement[] = flatten(
            useEl.map(e => D(e).asElementArray())
        ).map(e => e.get(0)).reduce((arr,x) => {
            if(x instanceof HTMLElement) return arr.concat(x);
            else return arr;
        },[])
            

        // ? Questa versione non può essere usata perchè Element[] di CollectionTarget fa impazzire la firma di map
        // const collection = isArray(el) ? 
        //     flatten(el.map((e: CollectionTarget) => {
        //         return D(e).asElementArray();
        //     } )) :
        //     D(el).asElementArray();
       
        const extracted = collection.map(e => this.extractAndCreate(e)).
            filter(e => e != null).filter(e => !e.element.isAlreadyParsed()).map(e => {
                e.element.setParsed();
                return e;
            });
        
        extracted.forEach((ewd) => {
            const group = ewd.attachData.getGroupName();
            if(!hasOwnProperty(this.groupedElements, group) || 
                    !isArray(this.groupedElements[group])) {
                this.groupedElements[group] = [];
            }
            this.groupedElements[group].push(ewd);
            if(trigger) {
                ewd.element.on(trigger, () => {
                    if(ewd.element.isReady()) {
                        this.action(ewd);
                    }
                });
            }
        });


        return this.afterAttach(extracted);
    }

    public updatedElementStatuses(updatedData: AttachAndStatusData<StatusData, T>[]): void {
        updatedData.forEach((ud) => {
            const group = ud.attachData.getGroupName();
            if(hasOwnProperty(this.groupedElements, group)) {
                this.groupedElements[group].forEach((el) => {
                    el.element.setStatus(ud.status);
                    el.element.setReady();
                    el.element.setContentRestrictionCriteria(ud.restriction)
                });
                this.afterUpdate(ud, this.groupedElements[group].map(x => x.element));
            }
        }, []);
        
    }

}